import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea
} from 'recharts';
import './MeanChartWeight.css';

const MeanChartWeight = ({ measurements, scheduleTargets }) => {
    // Define default periods based on mockup if none in schedule targets
    const periods = useMemo(() => {
        if (scheduleTargets?.weightTimePeriod && scheduleTargets.weightTimePeriod.length > 0) {
            return scheduleTargets.weightTimePeriod.map(p => ({
                name: p.name,
                range: `${p.start_time || '07:00 AM'} ~ ${p.end_time || '11:59 AM'}`
            }));
        }
        return [
            { name: 'Morning', range: '07:00 AM ~ 11:59 AM' },
            { name: 'Afternoon', range: '12:00 PM ~ 05:59 PM' },
            { name: 'Evening', range: '06:00 PM ~ 06:59 AM' }
        ];
    }, [scheduleTargets]);

    // Calculate mean values by period
    const periodMeans = useMemo(() => {
        if (!measurements || measurements.length === 0) {
            return periods.map(p => ({ ...p, weight: null }));
        }

        return periods.map(p => {
            const periodName = p.name;
            const periodMeasurements = measurements.filter(m => {
                const mPeriod = m.period_name || m.period || 'Unknown';
                if (mPeriod.toLowerCase() === periodName.toLowerCase()) return true;

                if ((!m.period_name && !m.period) && (m.measure_new_date_time || m.measurement_time || m.time_recorded)) {
                    const timeStr = (m.measure_new_date_time?.split('T')[1] || m.measurement_time || m.time_recorded || '').substring(0, 2);
                    const hour = parseInt(timeStr, 10);
                    if (!isNaN(hour)) {
                      if (periodName === 'Morning' && hour >= 7 && hour < 12) return true;
                      if (periodName === 'Afternoon' && hour >= 12 && hour < 18) return true;
                      if (periodName === 'Evening' && (hour >= 18 || hour < 7)) return true;
                    }
                }
                return false;
            });

            if (periodMeasurements.length === 0) {
                return { ...p, weight: null };
            }

            const weightValues = periodMeasurements
                .map(m => parseFloat(m.weight))
                .filter(v => !isNaN(v));

            const weightMean = weightValues.length > 0
                ? weightValues.reduce((a, b) => a + b, 0) / weightValues.length
                : null;

            return { ...p, weight: weightMean };
        });
    }, [measurements, periods]);

    const chartData = useMemo(() => {
      return periodMeans.map(p => ({
        name: p.name,
        Weight: p.weight != null ? Number(p.weight.toFixed(1)) : null
      }));
    }, [periodMeans]);

    const targetMin = scheduleTargets?.weightTargetRange?.weightMin || 66;
    const targetMax = scheduleTargets?.weightTargetRange?.weightMax || 220;

    return (
        <div className="mcw-chart-section" style={{ border: "none", padding: "10px 0 0 0", background: "transparent" }}>
            <div className="mcw-chart-container">
                <div className="mcw-chart-legend" style={{ justifyContent: "flex-end", marginBottom: "10px" }}>
                    <div className="mcw-legend-item">
                        <div className="mcw-legend-dot mcw-legend-weight" style={{ background: "#0077b6" }}></div>
                        <span style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>Weight (lbs)</span>
                    </div>
                </div>

                <div className="mcw-chart-area" style={{ border: "1px solid #e2e8f0", borderRadius: "8px", background: "#ffffff", padding: "16px 12px 10px 12px" }}>
                    {periodMeans.every(p => p.weight == null) ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: "#64748b" }}>No data available for the selected period</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={290}>
                          <LineChart data={chartData} margin={{ top: 20, right: 25, left: 15, bottom: 45 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis
                              dataKey="name"
                              interval={0}
                              height={45}
                              tick={{ fontSize: 11, fill: "#475569", fontWeight: 500 }}
                            />
                            <YAxis
                              tick={{ fontSize: 11, fill: "#475569" }}
                              label={{
                                value: "Weight (lbs)",
                                angle: -90,
                                position: "insideLeft",
                                offset: 0,
                                style: { fontSize: 11, fontWeight: 600, fill: "#334155", textAnchor: "middle" }
                              }}
                            />
                            <Tooltip
                              contentStyle={{ background: "#ffffff", borderRadius: "8px", border: "1px solid #cbd5e1", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", fontSize: "12px" }}
                              formatter={(val) => [val != null ? `${val} lbs` : "No reading", "Weight"]}
                            />
                            <ReferenceArea
                              y1={targetMin}
                              y2={targetMax}
                              fill="#f0fdf4"
                              fillOpacity={0.85}
                              stroke="#bbf7d0"
                              strokeDasharray="2 2"
                              label={{ value: `Target Range (${targetMin}–${targetMax} lbs)`, position: "insideTopRight", fill: "#16a34a", fontSize: 10, fontWeight: 600 }}
                            />
                            <Line
                              type="monotone"
                              dataKey="Weight"
                              stroke="#0077b6"
                              strokeWidth={2.5}
                              dot={{ r: 5, fill: "#0077b6", stroke: "#ffffff", strokeWidth: 2 }}
                              activeDot={{ r: 7 }}
                              connectNulls={true}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MeanChartWeight;
